type FilterValueFormatter = (value: string) => string;

/** Empty / dash placeholders are treated as the same missing-value option as the null sentinel. */
export function isFilterNullishRawValue(value: string, nullSentinel?: string): boolean {
    if (nullSentinel === undefined) {
        return false;
    }
    return value === nullSentinel || value === "-" || value.trim() === "";
}

export function normalizeFilterNullishRawValue(value: string, nullSentinel: string): string {
    if (isFilterNullishRawValue(value, nullSentinel) === true) {
        return nullSentinel;
    }
    return value;
}

export function normalizeFilterNullishRawValues(
    values: ReadonlyArray<string>,
    nullSentinel: string,
): Array<string> {
    const normalized = new Set<string>();
    for (const value of values) {
        normalized.add(normalizeFilterNullishRawValue(value, nullSentinel));
    }
    return Array.from(normalized);
}

export function excludeValuesFromFilterPicker(
    values: ReadonlyArray<string>,
    excludeValues: ReadonlyArray<string> | undefined,
): Array<string> {
    if (excludeValues === undefined || excludeValues.length === 0) {
        return [...values];
    }
    const excluded = new Set(excludeValues.map((value) => String(value)));
    return values.filter((value) => excluded.has(String(value)) === false);
}

export function mergeFilterColumnRawValues(
    queryValues: ReadonlyArray<string>,
    appliedFilterValues: ReadonlyArray<string> | undefined,
    nullSentinel?: string,
): Array<string> {
    const values = new Set<string>();
    const addValue = (value: string) => {
        if (nullSentinel !== undefined && isFilterNullishRawValue(value, nullSentinel) === true) {
            values.add(nullSentinel);
            return;
        }
        values.add(value);
    };
    for (const value of queryValues) {
        addValue(value);
    }
    if (appliedFilterValues !== undefined) {
        for (const value of appliedFilterValues) {
            addValue(value);
        }
    }
    return Array.from(values);
}

export function dedupeFilterValuesByDisplayLabel(
    values: ReadonlyArray<string>,
    valueFormatter: FilterValueFormatter,
): Array<string> {
    const seenLabels = new Set<string>();
    const deduped: Array<string> = [];
    for (const value of [...values].sort((left, right) => left.localeCompare(right))) {
        const label = valueFormatter(value);
        if (seenLabels.has(label) === true) {
            continue;
        }
        seenLabels.add(label);
        deduped.push(value);
    }
    return deduped;
}

export function filterValuesSharingDisplayLabel(
    targetValue: string,
    allValues: ReadonlyArray<string>,
    valueFormatter: FilterValueFormatter,
    nullSentinel?: string,
): Array<string> {
    if (nullSentinel !== undefined && targetValue === nullSentinel) {
        return [nullSentinel];
    }
    const targetLabel = valueFormatter(targetValue);
    return allValues.filter((value) => {
        if (nullSentinel !== undefined && value === nullSentinel) {
            return false;
        }
        return valueFormatter(value) === targetLabel;
    });
}

export function isAnyFilterValueWithDisplayLabelSelected(
    pickerValue: string,
    selectedValues: ReadonlyArray<string> | null,
    allValues: ReadonlyArray<string>,
    valueFormatter: FilterValueFormatter,
    nullSentinel?: string,
): boolean {
    if (selectedValues === null) {
        return true;
    }
    const group = filterValuesSharingDisplayLabel(
        pickerValue,
        allValues,
        valueFormatter,
        nullSentinel,
    );
    return group.some((value) => selectedValues.includes(value) === true);
}

export function countUniqueSelectedFilterDisplayLabels(
    selectedValues: ReadonlyArray<string>,
    valueFormatter: FilterValueFormatter,
    nullSentinel?: string,
): number {
    if (nullSentinel === undefined) {
        return new Set(selectedValues.map((value) => valueFormatter(value))).size;
    }
    const nullLabel = valueFormatter(nullSentinel);
    const seenLabels = new Set<string>();
    let includesNullSentinel = false;
    for (const value of selectedValues) {
        if (isFilterNullishRawValue(value, nullSentinel) === true) {
            includesNullSentinel = true;
            continue;
        }
        const label = valueFormatter(value);
        if (label === nullLabel || label.trim() === "") {
            includesNullSentinel = true;
            continue;
        }
        seenLabels.add(label);
    }
    return seenLabels.size + (includesNullSentinel === true ? 1 : 0);
}

export function toggleFilterValuesByDisplayLabel(
    pickerValue: string,
    selectedValues: ReadonlyArray<string> | null,
    allValues: ReadonlyArray<string>,
    valueFormatter: FilterValueFormatter,
    nullSentinel?: string,
): Array<string> | null {
    const group = filterValuesSharingDisplayLabel(
        pickerValue,
        allValues,
        valueFormatter,
        nullSentinel,
    );
    if (selectedValues === null) {
        const groupSet = new Set(group);
        const remaining = allValues.filter((value) => groupSet.has(value) === false);
        return remaining;
    }
    const isSelected = group.some((value) => selectedValues.includes(value) === true);
    if (isSelected === true) {
        return selectedValues.filter((value) => group.includes(value) === false);
    }
    return [
        ...selectedValues,
        ...group.filter((value) => selectedValues.includes(value) === false),
    ];
}

export function buildFilterDisplayLabelIndex(
    allValues: ReadonlyArray<string>,
    valueFormatter: FilterValueFormatter,
): Map<string, Array<string>> {
    const map = new Map<string, Array<string>>();
    for (const value of allValues) {
        const label = valueFormatter(value);
        const group = map.get(label);
        if (group === undefined) {
            map.set(label, [value]);
        } else {
            group.push(value);
        }
    }
    return map;
}

export function isFilterPickerValueSelected(args: {
    columnValue: string;
    selectedValues: ReadonlyArray<string> | null;
    selectedValuesSet: Set<string> | null;
    valueFormatter?: FilterValueFormatter;
    displayLabelIndex?: Map<string, Array<string>> | null;
    nullSentinel?: string;
}): boolean {
    if (args.selectedValues === null) {
        return true;
    }
    if (
        args.nullSentinel !== undefined
        && isFilterNullishRawValue(args.columnValue, args.nullSentinel) === true
    ) {
        return args.selectedValues.some(
            (value) => isFilterNullishRawValue(value, args.nullSentinel) === true,
        );
    }
    if (args.valueFormatter === undefined) {
        return args.selectedValuesSet?.has(args.columnValue) === true;
    }
    const label = args.valueFormatter(args.columnValue);
    const group = args.displayLabelIndex?.get(label) ?? [];
    return group.some((value) => args.selectedValuesSet?.has(value) === true);
}

export function countSelectedFilterPickerValues(args: {
    pickerValues: ReadonlyArray<string>;
    selectedValues: ReadonlyArray<string> | null;
    selectedValuesSet: Set<string> | null;
    valueFormatter?: FilterValueFormatter;
    displayLabelIndex?: Map<string, Array<string>> | null;
    nullSentinel?: string;
}): number {
    let count = 0;
    for (const columnValue of args.pickerValues) {
        if (
            isFilterPickerValueSelected({
                columnValue: columnValue,
                selectedValues: args.selectedValues,
                selectedValuesSet: args.selectedValuesSet,
                valueFormatter: args.valueFormatter,
                displayLabelIndex: args.displayLabelIndex,
                nullSentinel: args.nullSentinel,
            }) === true
        ) {
            count = count + 1;
        }
    }
    return count;
}

/**
 * Resolves ephemeral filter selection after "Select all".
 * Uses `null` (no filter / all selected) instead of materializing every value when possible.
 */
export function resolveSelectAllEphemeralLineItems(args: {
    searchQuery: string;
    filteredPickerValues: ReadonlyArray<string>;
    allRawValues: ReadonlyArray<string>;
    previousLineItems: ReadonlyArray<string> | null;
    valueFormatter?: FilterValueFormatter;
    nullSentinel?: string;
}): Array<string> | null {
    if (args.searchQuery.trim() === "") {
        return null;
    }
    const merged = new Set(args.previousLineItems ?? args.allRawValues);
    if (args.valueFormatter === undefined) {
        for (const value of args.filteredPickerValues) {
            merged.add(value);
        }
    } else {
        for (const pickerValue of args.filteredPickerValues) {
            const group = filterValuesSharingDisplayLabel(
                pickerValue,
                args.allRawValues,
                args.valueFormatter,
                args.nullSentinel,
            );
            for (const value of group) {
                merged.add(value);
            }
        }
    }
    if (merged.size >= args.allRawValues.length) {
        return null;
    }
    return Array.from(merged);
}

export function buildFilterColumnValuesForPicker(args: {
    queryValues: ReadonlyArray<string>;
    appliedFilterValues: ReadonlyArray<string> | undefined;
    nullSentinel: string;
    valueFormatter?: FilterValueFormatter;
}): {
    allRawValues: Array<string>;
    pickerValues: Array<string>;
} {
    const mergedValues = mergeFilterColumnRawValues(
        args.queryValues,
        args.appliedFilterValues,
        args.nullSentinel,
    );
    const hasNullValues = mergedValues.includes(args.nullSentinel);
    const nonNullValues = mergedValues.filter((value) => value !== args.nullSentinel);
    const sortedNonNullValues = [...nonNullValues].sort((left, right) => left.localeCompare(right));

    if (args.valueFormatter === undefined) {
        const allRawValues = hasNullValues ? [args.nullSentinel, ...sortedNonNullValues] : sortedNonNullValues;
        return {
            allRawValues,
            pickerValues: allRawValues,
        };
    }

    const nullDisplayLabel = args.valueFormatter(args.nullSentinel);
    const nonNullValuesExcludingNullDisplayLabel =
        hasNullValues === true
            ? sortedNonNullValues.filter((value) => args.valueFormatter!(value) !== nullDisplayLabel)
            : sortedNonNullValues;
    const allRawValues =
        hasNullValues === true
            ? [args.nullSentinel, ...nonNullValuesExcludingNullDisplayLabel]
            : nonNullValuesExcludingNullDisplayLabel;
    const dedupedNonNullValues = dedupeFilterValuesByDisplayLabel(
        nonNullValuesExcludingNullDisplayLabel,
        args.valueFormatter,
    );
    return {
        allRawValues,
        pickerValues: hasNullValues ? [args.nullSentinel, ...dedupedNonNullValues] : dedupedNonNullValues,
    };
}
